import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Typography,
  useTheme,
} from "@mui/material";

import BecomeDealerCard from "@/components/BecomeDealer/BecomeDealerSteps/StepperCards/BecomeDealerCard";
import BenefitCard from "@/components/Home/Benefits/BenefitCard";
import StepperIcon from "./StepperIcon";
import { getStepContentStyles, getStepperStyles } from "./styles";

interface IStep {
  title: string;
  description: string;
  imageUrl?: string;
  thumbnail?: string;
  maxWidth?: number;
  Icon: React.ElementType;
}

interface IProps {
  steps: IStep[];
  type: "benefits" | "dealer";
}

const CustomStepper: React.FC<IProps> = ({ steps, type }) => {
  const theme = useTheme();

  const [activeStep, setActiveStep] = useState<number>(0);
  const stepperRef = useRef<HTMLElement>();
  const containerRef = useRef<HTMLElement>();

  // Function to advance to next step (for dealer type)
  const goToNextStep = () => {
    if (activeStep < steps.length - 1) {
      if (type === "dealer") {
        const currentScrollY = window.scrollY;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        
        if (containerRef.current) {
          const stepperTop = containerRef.current.offsetTop;
          const stepperHeight = containerRef.current.offsetHeight;
          
          if (currentScrollY > stepperTop + (stepperHeight / 2)) {
            const step2Element = document.getElementById('step-1');
            if (step2Element) {
              const step2Top = step2Element.offsetTop;
              const offset = 100; // pixels from top
              
              window.scrollTo({
                top: step2Top - offset,
                behavior: 'smooth'
              });
            }
          }
        } else {
          const scrollPercentage = currentScrollY / (documentHeight - windowHeight);
          if (scrollPercentage > 0.6) {
            const step2Element = document.getElementById('step-1');
            if (step2Element) {
              step2Element.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'start' 
              });
            }
          }
        }
      }
      
      setActiveStep(activeStep + 1);
    }
  };

  // Handling scroll events (only for benefits type)
  useEffect(() => {
    if (type === "benefits") {
      const handleScroll = () => {
        if (stepperRef.current && containerRef.current) {
          const value =
            stepperRef.current.offsetTop - containerRef.current.offsetTop;

          if (value >= 0 && value < 1200) {
            const step = Math.floor(value / 300);
            setActiveStep(step);
          }
        }
      };

      // Add scroll event listener
      window.addEventListener("scroll", handleScroll);

      // Cleanup function to remove scroll event listener
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [activeStep, type]);

  // Auto-scroll to step 2 for dealer type when step changes
  useEffect(() => {
    if (type === "dealer" && activeStep === 1) {
      // Small delay to ensure the step content is rendered
      setTimeout(() => {
        const step2Element = document.getElementById('step-1');
        if (step2Element) {
          const currentScrollY = window.scrollY;
          const windowHeight = window.innerHeight;
          const documentHeight = document.documentElement.scrollHeight;
          
          // Only scroll if user is in the bottom half of the page
          if (currentScrollY > (documentHeight - windowHeight) * 0.5) {
            step2Element.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }
        }
      }, 100);
    }
  }, [activeStep, type]);

  return (
    <Box
      sx={{
        maxWidth: { xs: 400, md: 695 },
        height: type === "dealer" ? "auto" : "2000px"
      }}
      mx="auto"
      ref={containerRef}
    >
      <Box ref={stepperRef} sx={{ position: type === "dealer" ? "static" : "sticky", top: 0 }}>
        <Stepper
          activeStep={activeStep}
          orientation="vertical"
          sx={getStepperStyles(theme)}
        >
          {steps.map(
            (
              { title, description, imageUrl, thumbnail, Icon, maxWidth },
              index,
              array
            ) => (
              <Step
                key={index}
                id={`step-${index}`}
                className="step"
                sx={{
                  display: {
                    xs: type === "dealer" ? "block" : (index < activeStep ? "none" : "block"),
                    sm: type === "dealer" ? "block" : "block",
                    md: type === "dealer" ? "block" : (index < activeStep ? "none" : "block"),
                    xl: type === "dealer" ? "block" : "block",
                  },
                }}
              >
                <StepLabel
                  icon={
                    <StepperIcon
                      IconComponent={Icon}
                      isLast={array.length - 1 === index}
                    />
                  }
                  sx={{
                    "& .MuiStepLabel-labelContainer": {
                      paddingBottom: type === "dealer" && index === 1 ? 0 : undefined,
                      marginTop: type === "dealer" && index === 1 ? "8px" : undefined
                    }
                  }}
                >
                  {activeStep !== index && type !== "dealer" && (
                    <>
                      <Typography
                        variant="h4"
                        component="h3"
                        color="text.primary"
                        mb={{ xs: index < activeStep ? 0 : 1, md: 1 }}
                      >
                        {title}
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        fontSize={{ sm: 16 }}
                        sx={{
                          display: {
                            xs: index < activeStep ? "none" : "block",
                            sm: "block",
                            md: index < activeStep ? "none" : "block",
                            xl: "block",
                          },
                        }}
                      >
                        {description}
                      </Typography>
                    </>
                  )}
                  {type === "dealer" && activeStep !== index && (
                    <>
                      <Typography
                        variant="h4"
                        component="h3"
                        color={index < activeStep ? "text.secondary" : "text.primary"}
                        mb={{ xs: 1, md: 1 }}
                      >
                        {title}
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        fontSize={{ sm: 16 }}
                      >
                        {description}
                      </Typography>
                    </>
                  )}
                </StepLabel>
                <StepContent
                  sx={getStepContentStyles(theme, index === array.length - 1)}
                >
                  {type === "benefits" &&
                    imageUrl &&
                    thumbnail &&
                    activeStep === index && (
                      <BenefitCard
                        title={title}
                        description={description}
                        imageUrl={imageUrl}
                        thumbnail={thumbnail}
                        maxWidth={maxWidth}
                      />
                    )}
                  {type === "dealer" && activeStep === index && (
                    <BecomeDealerCard
                      cardIndex={index}
                      title={title}
                      description={description}
                      goToNextStep={goToNextStep}
                    />
                  )}

                </StepContent>
              </Step>
            )
          )}
        </Stepper>
      </Box>
    </Box>
  );
};

export default CustomStepper;
